import { Trans, useTranslation } from 'react-i18next';
import { printVisibility } from '@/features/styles/lib/printStyles';
import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  MotionBox,
  Portal,
  Text,
} from '@/ui';
import { StepProgress } from './StepProgress';
import { SECTION_COUNT, type Step } from './steps';

type Props = {
  step: Step;
  isFirstStep: boolean;
  isLastStep: boolean;
  /** Held back while the card is still walking to the control it describes. */
  isNextDisabled: boolean;
  /** Runs the one action a card's copy can name inline, if it names one. */
  onBodyAction: () => void;
  /** Clear of the Send sidebar, which is the one panel on the card's side. */
  isShiftedForSidebar: boolean;
  /** Above the Share modal's overlay, so Next stays reachable beside it. */
  isAboveModal: boolean;
  onBack: () => void;
  onNext: () => void;
  onClose: () => void;
};

export const OnboardingCard = ({
  step,
  isFirstStep,
  isLastStep,
  isNextDisabled,
  isShiftedForSidebar,
  isAboveModal,
  onBack,
  onBodyAction,
  onNext,
  onClose,
}: Props) => {
  const { t } = useTranslation('plannerOnboarding');

  return (
    <Portal>
      <MotionBox
        animate={{ opacity: 1, y: 0 }}
        bottom={6}
        boxShadow="0 16px 40px rgba(20, 14, 46, 0.22)"
        exit={{ opacity: 0, y: 12 }}
        initial={{ opacity: 0, y: 12 }}
        layerStyle="ahCard"
        maxW="calc(100vw - 48px)"
        p={5}
        position="fixed"
        right={isShiftedForSidebar ? 'calc(400px + var(--chakra-space-6))' : 6}
        // Matches the Send sidebar's own open/close timing so the two move together.
        sx={{ ...printVisibility.hide(), transition: 'right 0.22s ease' }}
        transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
        w="340px"
        zIndex={isAboveModal ? 'calc(var(--chakra-zIndices-modal) + 2)' : 25}
      >
        <Flex align="flex-start" gap={3}>
          <Text
            color="magicPurple.600"
            fontWeight="bold"
            textStyle="ahLabel"
            textTransform="uppercase"
          >
            {/* The guide's four steps are what the progress bar counts, so this
                is the only place a bare "of 4" appears. */}
            {t('eyebrow', {
              current: step.sectionIndex + 1,
              total: SECTION_COUNT,
              section: t(`sections.${step.section}`),
            })}
          </Text>
          <IconButton
            aria-label={t('close')}
            icon={<Icon icon="X" size={3.5} />}
            ml="auto"
            mr={-2}
            mt={-2}
            size="ah-sm"
            variant="ahGhost"
            onClick={onClose}
          />
        </Flex>

        <Text color="ink.strong" fontSize="lg" fontWeight="bold" mt={1}>
          {t(`steps.${step.id}.title`)}
        </Text>
        <Text color="ink.soft" mt={1.5} textStyle="ahControl">
          {/* Copy that names a control the author could press gets to be that
              control. Only the Magic card carries an <action> tag today; for
              every other card this renders the sentence unchanged. */}
          <Trans
            i18nKey={`steps.${step.id}.body`}
            t={t}
            components={{
              action: (
                <Button
                  color="magicPurple.600"
                  fontSize="inherit"
                  fontWeight={700}
                  textDecor="underline"
                  variant="link"
                  onClick={onBodyAction}
                />
              ),
            }}
          />
        </Text>

        <Flex align="center" gap={3} mt={5}>
          <Box>
            {/* Named as a position inside the step above rather than a count of
                its own, so it cannot be read as a second progression. A section
                holding one card has no subset to describe. */}
            {step.stepsInSection > 1 && (
              <Text
                color="ink.muted"
                fontWeight="bold"
                mb={1.5}
                textStyle="ahLabel"
                textTransform="uppercase"
              >
                {t('part', {
                  position: step.positionInSection,
                  total: step.stepsInSection,
                })}
              </Text>
            )}
            <StepProgress sectionIndex={step.sectionIndex} />
          </Box>

          <Flex gap={2} ml="auto">
            {!isFirstStep && (
              <Button size="ah-sm" variant="ahOutlineSubtle" onClick={onBack}>
                {t('back')}
              </Button>
            )}
            <Button
              isDisabled={isNextDisabled}
              size="ah-sm"
              variant="ahPrimary"
              onClick={onNext}
            >
              {isLastStep ? t('done') : t('next')}
            </Button>
          </Flex>
        </Flex>
      </MotionBox>
    </Portal>
  );
};
